import { useCallback, useMemo } from 'react';
import { APP_NAME_PATTERN } from '../../utils/customApp';
import { useModules } from './useModules';
import type { ModulesApiResponse } from './useModules';

/** `appName` → the remote that serves it, or `undefined` for "its own id". */
export type RemoteAppLookup = (appName: string) => string | undefined;

/** `appName` → its registered display name, or `undefined` when unnamed. */
export type AppNameLookup = (appName: string) => string | undefined;

/** `appName` → the studio repo that serves its bundle, or `undefined` for the default. */
export type StudioLookup = (appName: string) => string | undefined;

/** `entityName` → the registered app that declares it, or `undefined`. */
export type AppIdForEntityLookup = (entityName: string) => string | undefined;

export interface AppRegistry {
  remoteAppFor: RemoteAppLookup;
  /**
   * The registered app whose `metadata.entityName` names this entity (DP-6873).
   *
   * Backs the cold-link fallback of the details page's back affordance: with no
   * SPA history to return through, the entity's declared home app is the next
   * best target. Resolves only when exactly one app declares the entity —
   * several declaring it is genuinely ambiguous, and guessing would send the
   * user into the wrong app, so the lookup returns `undefined` and the caller
   * keeps the entity-list fallback. `entityName` reaches the payload only on
   * backends that forward it into the `/modules` apps map; on older backends
   * every entry lacks it and this resolves nothing, which is today's behaviour.
   */
  appIdForEntity: AppIdForEntityLookup;
  /**
   * The studio (`appConfig.studio`, DP-8112) whose bundle serves the app, e.g.
   * `encore-ikea-apps`. `undefined` means the default `encore-apps-studio`.
   * For an aliased app (`remoteApp`) the target row decides — that row owns
   * the bundle, so a target without `studio` means `encore-apps-studio`. The
   * alias row's own studio applies only when the target has no registry row.
   */
  studioFor: StudioLookup;
  /**
   * The human name a client gave the app when registering it — the same string
   * the left nav shows.
   *
   * Custom-app routes are keyed by the registry id, and a client is free to
   * register one under an opaque id (a UUID, on the tenants that generate them
   * rather than hand-writing slugs). Passing that id on as the app's title
   * surfaces it verbatim to the user: the Genie header rendered
   * "Locus 01f1a4e407c51a20a4e55c694f2ee1a5" because the id was the only thing
   * the host ever sent.
   *
   * `undefined` when the registry has no name for the id, so callers keep
   * whatever they show today rather than rendering a blank title.
   */
  nameFor: AppNameLookup;
  /**
   * `false` only while the first `/modules` fetch is in flight. Callers hold the
   * MFE mount until then: resolving early with the fallback would register the
   * remote under a wrong manifest URL and fail loudly before the alias arrives.
   * A fetch error or a missing clientId counts as ready — the lookup is then
   * empty and every app falls back to its own id, which is today's behaviour.
   */
  isReady: boolean;
}

/** The `apps` map carried by the `/modules` envelope, if the payload has one. */
function appsOf(response: ModulesApiResponse | undefined): Record<
  string,
  {
    name?: unknown;
    remoteApp?: unknown;
    entityName?: unknown;
    studio?: unknown;
  } | null
> {
  const apps = (response?.data as { apps?: unknown } | undefined)?.apps;
  return typeof apps === 'object' && apps !== null
    ? (apps as Record<
        string,
        {
          name?: unknown;
          remoteApp?: unknown;
          entityName?: unknown;
          studio?: unknown;
        } | null
      >)
    : {};
}

/**
 * Project the `/modules` `apps` map into `id → remoteApp`. Only entries that
 * actually redirect are kept: a `remoteApp` equal to the app's own id, a
 * non-string, or a value outside `APP_NAME_PATTERN` (it becomes a URL path
 * segment) is treated as absent. Pure, so it is unit-testable off the network.
 */
export function buildRemoteAppMap(
  response: ModulesApiResponse | undefined
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const [id, record] of Object.entries(appsOf(response))) {
    const remoteApp = record?.remoteApp;
    if (
      typeof remoteApp === 'string' &&
      remoteApp !== id &&
      APP_NAME_PATTERN.test(remoteApp)
    ) {
      map.set(id, remoteApp);
    }
  }
  return map;
}

/**
 * Project the `/modules` `apps` map into `id → studio`. The value becomes a URL
 * path/host segment, so one outside `APP_NAME_PATTERN` or over
 * `STUDIO_MAX_LENGTH` is treated as absent and logged — the backend rejects it
 * on write, so seeing one means bad data, and it must never build a URL.
 */
const STUDIO_MAX_LENGTH = 50; // the apps schema's maxLength for appConfig.studio

export function buildStudioMap(
  response: ModulesApiResponse | undefined
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const [id, record] of Object.entries(appsOf(response))) {
    const studio = record?.studio;
    if (studio === undefined || studio === null) {
      // absent: nothing to record or log
    } else if (
      typeof studio === 'string' &&
      studio.length <= STUDIO_MAX_LENGTH &&
      APP_NAME_PATTERN.test(studio)
    ) {
      map.set(id, studio);
    } else {
      // eslint-disable-next-line no-console
      console.error(`Ignoring invalid studio for app "${id}"`);
    }
  }
  return map;
}

/**
 * Project the `/modules` `apps` map into `id → name`.
 *
 * Only entries that carry a usable name are kept: a missing, non-string or
 * blank `name` is treated as absent so `nameFor` returns `undefined` and the
 * caller falls back to the id, rather than rendering an empty title.
 * Deliberately does NOT constrain the name to `APP_NAME_PATTERN` — that
 * grammar governs ids, which become URL path segments; a display name is free
 * text ("Order & Fulfilment Agent") and validating it against the id grammar
 * would reject every realistic name.
 */
export function buildAppNameMap(
  response: ModulesApiResponse | undefined
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const [id, record] of Object.entries(appsOf(response))) {
    const name = record?.name;
    if (typeof name === 'string' && name.trim()) {
      map.set(id, name.trim());
    }
  }
  return map;
}

/**
 * Project the `/modules` `apps` map into `entityName → appId`, keeping only
 * entities that exactly one app declares (DP-6873). An entity declared by two
 * apps is dropped rather than resolved arbitrarily — the tombstone (`null`)
 * records the collision so a third declaration cannot resurrect the first.
 * The stored key is trimmed, matching {@link buildAppNameMap}; the id's URL
 * grammar is checked at lookup, so an invalid declarer still counts toward
 * ambiguity.
 */
export function buildEntityAppMap(
  response: ModulesApiResponse | undefined
): ReadonlyMap<string, string | null> {
  const map = new Map<string, string | null>();
  for (const [id, record] of Object.entries(appsOf(response))) {
    const entityName = record?.entityName;
    if (typeof entityName !== 'string' || !entityName.trim()) {
      continue;
    }
    const key = entityName.trim();
    map.set(key, map.has(key) ? null : id);
  }
  return map;
}

/**
 * The client's app registry, reduced to the three lookups the custom-app hosts
 * need (`CustomAppPage`, `EntityGridView`, `EntityDetails`,
 * `CustomAppActionModal`): which bundle serves an app, what to call it, and
 * which app is an entity's home.
 *
 * Read from the `/modules` payload the dashboard already fetches (DP-5201). It
 * used to be a search of the `apps` entity, which needs an entity read scope
 * that client users do not have — their first page load answered 403, and
 * both lookups came back empty for exactly the users who see custom apps. The
 * backend serves the map from its own registry cache, so it covers every
 * registered app, not just the ones reachable from the navigation tree:
 * custom apps also mount from workflow actions and from entity tabs.
 */
export function useAppRegistry(clientId: string): AppRegistry {
  const { data, isLoading } = useModules(clientId);

  const remoteApps = useMemo(() => buildRemoteAppMap(data), [data]);
  const remoteAppFor = useCallback<RemoteAppLookup>(
    appName => remoteApps.get(appName),
    [remoteApps]
  );

  // Same payload, same memo dependency as the alias map — reading the name is
  // a second projection of one response, not a second fetch.
  const appNames = useMemo(() => buildAppNameMap(data), [data]);
  const nameFor = useCallback<AppNameLookup>(
    appName => appNames.get(appName),
    [appNames]
  );

  const studios = useMemo(() => buildStudioMap(data), [data]);
  const registered = useMemo(
    () =>
      new Set(
        Object.entries(appsOf(data))
          .filter(([, record]) => record)
          .map(([id]) => id)
      ),
    [data]
  );
  const studioFor = useCallback<StudioLookup>(
    appName => {
      const target = remoteApps.get(appName);
      // A registered target owns the bundle, studio or not (none = encore-apps-studio).
      return target && registered.has(target)
        ? studios.get(target)
        : studios.get(appName);
    },
    [studios, remoteApps, registered]
  );

  const entityApps = useMemo(() => buildEntityAppMap(data), [data]);
  const appIdForEntity = useCallback<AppIdForEntityLookup>(
    entityName => {
      // The id becomes a URL path segment (`/apps/{id}`), same reason
      // buildRemoteAppMap checks APP_NAME_PATTERN.
      const id = entityApps.get(entityName);
      return id && APP_NAME_PATTERN.test(id) ? id : undefined;
    },
    [entityApps]
  );

  return {
    remoteAppFor,
    studioFor,
    nameFor,
    appIdForEntity,
    isReady: !isLoading,
  };
}
